// src/utils/privileges.js

/**
 * Utility functions for working with user privileges
 */

/**
 * Check if the user has a specific privilege
 * @param {string} privilegeName - The name of the privilege to check
 * @returns {boolean} - True if the user has the privilege, false otherwise
 */
export const hasPrivilege = (privilegeName) => {
    try {
      const privileges = JSON.parse(localStorage.getItem('location_service_privileges') || '[]');
      return Array.isArray(privileges) && privileges.includes(privilegeName);
    } catch (error) {
      console.error('Error checking privilege:', error);
      return false;
    }
  };
  
  /**
   * Check if the user has any of the specified privileges
   * @param {string[]} privilegeNames - Array of privilege names to check
   * @returns {boolean} - True if the user has any of the privileges, false otherwise
   */
  export const hasAnyPrivilege = (privilegeNames) => {
    try {
      const privileges = JSON.parse(localStorage.getItem('location_service_privileges') || '[]');
      return Array.isArray(privileges) && privilegeNames.some(name => privileges.includes(name));
    } catch (error) {
      console.error('Error checking privileges:', error);
      return false;
    }
  };
  
  /**
   * Check if the user has all of the specified privileges
   * @param {string[]} privilegeNames - Array of privilege names to check
   * @returns {boolean} - True if the user has all of the privileges, false otherwise
   */
  export const hasAllPrivileges = (privilegeNames) => {
    try {
      const privileges = JSON.parse(localStorage.getItem('location_service_privileges') || '[]');
      return Array.isArray(privileges) && 
             privilegeNames.every(name => privileges.includes(name));
    } catch (error) {
      console.error('Error checking privileges:', error);
      return false;
    }
  };
  
  /**
   * Get all privileges for the document service
   * @returns {string[]} - Array of privilege names
   */
  export const getAllPrivileges = () => {
    try {
      return JSON.parse(localStorage.getItem('location_service_privileges') || '[]');
    } catch (error) {
      console.error('Error getting privileges:', error);
      return [];
    }
  };
  
  /**
   * Filter a list of routes based on user privileges
   * @param {Array} routes - List of route objects with requiredPrivileges property
   * @returns {Array} - Filtered list of routes that the user has privileges for
   */
  export const filterRoutesByPrivileges = (routes) => {
    return routes.filter(route => {
      // If no privileges required, allow access
      if (!route.requiredPrivileges || route.requiredPrivileges.length === 0) {
        return true;
      }
      
      // Check if user has any of the required privileges
      return hasAnyPrivilege(route.requiredPrivileges);
    });
  };

  export default {
    hasPrivilege,
    hasAnyPrivilege,
    hasAllPrivileges,
    getAllPrivileges,
    filterRoutesByPrivileges
  };